import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * Autocomplete input backed by /api/alumni/suggest.
 * - `field` — "university" | "company" (required, drives the suggest endpoint)
 * - `value`, `onChange` — controlled string
 * - `placeholder`, `testid` — passed through
 * - Free-typing is always allowed; suggestions are hints, not a required constraint.
 */
export default function AlumniAutocomplete({
  field,
  value,
  onChange,
  placeholder = "",
  testid,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced fetch — 200ms
  useEffect(() => {
    if (disabled) return;
    const t = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort?.();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const r = await api.get(
          `/alumni/suggest?field=${encodeURIComponent(field)}&q=${encodeURIComponent(value || "")}`,
          { signal: ctrl.signal }
        );
        setItems(r.data?.suggestions || []);
      } catch (_) {
        // Silent — network hiccup shouldn't block typing
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [value, field, disabled]);

  // Close when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pick(items[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        type="text"
        placeholder={placeholder}
        value={value || ""}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onKeyDown={onKey}
        autoComplete="off"
        data-testid={testid}
        className="rounded-xl h-10"
      />
      {open && (items.length > 0 || loading) && (
        <div
          className="absolute left-0 right-0 mt-1 z-50 bg-white border border-[#EAE7E0] rounded-xl shadow-lg max-h-56 overflow-auto"
          data-testid={testid ? `${testid}-dropdown` : undefined}
        >
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-[#4B5563] italic">Mencari…</div>
          )}
          {items.map((it, i) => (
            <button
              key={it + i}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(it);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={testid ? `${testid}-item-${i}` : undefined}
              className={
                "w-full text-left px-3 py-2 text-sm transition-colors " +
                (activeIdx === i
                  ? "bg-[#EFF6FF] text-[#1E3A8A]"
                  : "text-[#1F2937] hover:bg-[#FAFAF8]")
              }
            >
              {it}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
